import React, { useState } from 'react';

import Collapse from '@material-ui/core/Collapse';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core/styles';

import clsx from 'clsx';
import PropTypes from 'prop-types';

import PanelBody from './components/PanelBody';
import PanelExpandButton from './components/PanelExpandButton';
import PanelHeader, { Actions, Title } from './components/PanelHeader';
import styles from './styles';

export const PanelContext = React.createContext({
  isOpen: true,
  toggleOpen: () => {},
});

export const Panel = props => {
  const {
    className,
    classes,
    children,
    isCollapsible,
    elevation = 1,
    title,
    headerActions,
    isClosedDefault = false,
    PanelHeaderProps = {},
    renderHeaderContent,
    PanelBodyProps = {},
    ...other
  } = props;

  const [isOpen, setIsOpen] = useState(!isCollapsible || !isClosedDefault);

  function toggleOpen(open) {
    setIsOpen(!open);
  }

  const PanelWrapper = ({ isCollapsible, isOpen, toggleOpen, children }) =>
    isCollapsible ? (
      <PanelContext.Provider value={{ isOpen, toggleOpen }}>{children}</PanelContext.Provider>
    ) : (
      <>{children}</>
    );

  return (
    <PanelWrapper isCollapsible={isCollapsible} isOpen={isOpen} toggleOpen={toggleOpen}>
      <Paper
        data-testid='panel'
        className={clsx(classes.root, className)}
        // Elevations can be initialized in theme.shadows
        elevation={elevation}
        square
        {...other}
      >
        <PanelHeader
          className={clsx(classes.header, 'Panel-Header')}
          title={title}
          actions={headerActions}
          isCollapsible={isCollapsible}
          renderHeaderContent={renderHeaderContent}
          {...PanelHeaderProps}
        />
        <Collapse in={isOpen}>
          <PanelBody className={clsx(classes.body, 'Panel-Body')} {...PanelBodyProps}>
            {children}
          </PanelBody>
        </Collapse>
      </Paper>
    </PanelWrapper>
  );
};

Panel.defaultProps = {
  elevation: 1,
  isClosedDefault: false,
};

Panel.propTypes = {
  /** Title of panel component */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.element, PropTypes.object]),
  /** Contents of the panel body */
  children: PropTypes.node,
  /** Actions to be included in the header row. */
  headerActions: PropTypes.node,
  /** Custom Render method to show Header. Dev is encouraged to use the
   * compound components, Panel.Actions, Panel.Title, and Panel.ExpandButton */
  renderHeaderContent: PropTypes.node,
  /** Enables collapsible ability */
  isCollapsible: PropTypes.bool,
  /** Indicates if Panel is collapsed or expanded by default */
  isClosedDefault: PropTypes.bool,
  /** Corresponds to dropshadow of panel. Shadows configurable in the theme */
  elevation: PropTypes.number,
  /** Attributes applied to the PanelHeader element */
  PanelHeaderProps: PropTypes.object,
  /** Attributes applied to the PanelBody element */
  PanelBodyProps: PropTypes.object,
  classes: PropTypes.object,
  className: PropTypes.string,
};

Panel.Actions = Actions;
Panel.Title = Title;
Panel.ExpandButton = PanelExpandButton;

export { default as Body } from './components/PanelBody';
export { default as Header } from './components/PanelHeader';
export default withStyles(styles)(Panel);
